<template>
  <v-text-field
    v-model="computedValue"
    class="closd-text-field"
    :class="{
      'closd-text-field--small': small,
    }"
    :color="color"
    :hide-details="hideDetails"
    clear-icon="close"
    density="compact"
    ref="input"
    variant="outlined"
    v-bind="$attrs"
    :append-inner-icon="appendInnerIcon"
    @click:append-inner="onAppendInnerClick"
    @click:clear="clear"
    @blur="$emit('blur')"
  >
    <template v-for="(_, slot) in $slots" v-slot:[slot]>
      <slot :name="slot" />
    </template>
  </v-text-field>
</template>

<script>
/**
 * A wrapper around the text field with default styling and custom default behavior
 * All Vuetify props for the text field component are usable.
 * @see https://vuetifyjs.com/en/api/v-text-field/#props
 */
export default {
  name: 'AppTextField',
  inheritAttrs: false,
  emits: [
    'clear',
    'blur',
    'update:model-value',
  ],
  props: {
    /**
     * @model
     * The input’s value
     */
    modelValue: {
      required: true,
    },
    /**
     * Applies specified color to the control
     * @default blue darken-2
     */
    color: {
      default: 'blue-darken-2',
    },
    /**
     * Hides hint and validation errors
     */
    hideDetails: {
      default: 'auto',
    },
    small: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    appendInnerIcon () {
      if (this.$attrs['append-inner-icon']) {
        return this.$attrs['append-inner-icon']
      }
      switch (this.$attrs.type) {
        case 'date':
          return 'calendar_today'
        case 'time':
          return 'schedule'
        default:
          return undefined
      }
    },
    computedValue: {
      get () {
        return this.modelValue
      },
      set (newValue) {
        /**
         * The updated bound model
         *
         * @property {string} newValue new value set
         */
        this.$emit('update:model-value', newValue)
      },
    },
  },
  methods: {
    clear () {
      this.$emit('clear')
    },
    onAppendInnerClick () {
      // For native date/time fields, the browser picker button is hidden via
      // CSS, so make our custom icon open the native picker instead.
      if (this.$attrs.type !== 'date' && this.$attrs.type !== 'time') {
        return
      }
      const nativeInput = this.$refs.input?.$el?.querySelector('input')
      nativeInput?.showPicker?.()
    },
  },
}
</script>

<style scoped lang="scss">
.closd-text-field.closd-text-field--small {
  min-width: 60px;
  :deep(.v-field__input) {
    min-height: 28px;
    padding-top: 3px;
  }

  :deep(.v-field__field) {
    height: 28px;
  }
}

.closd-text-field {
  :deep(.v-field) {
    --outline-color: rgb(var(--v-theme-border-secondary));
    --outline-width: 1px;
    --text-color: rgb(var(--v-theme-text-secondary));
    --background-color: rgb(var(--v-theme-surface-primary));
    --v-disabled-opacity: 1;
    --placeholder-color: rgb(var(--v-theme-text-placeholder));
    color: var(--text-color);
    background-color: var(--background-color);

    &:not(.v-field--disabled):hover {
      --outline-color: rgb(var(--v-theme-border-secondary-hover));
    }

    &:not(.v-field--disabled):focus-within {
      --outline-color: rgb(var(--v-theme-border-info-bold));
      --outline-width: 2px;
    }

    &.v-field--disabled {
      --text-color: rgb(var(--v-theme-text-disabled));
      --background-color: rgb(var(--v-theme-surface-disabled));
      --outline-color: rgb(var(--v-theme-border-disabled));
      --placeholder-color: rgb(var(--v-theme-text-disabled));
    }

    &.v-field--error {
      --outline-color: rgb(var(--v-theme-border-danger-bold)) !important;
    }

    .v-field-label:not(.v-field-label--floating) {
      color: var(--placeholder-color);
    }

    .v-field-label--floating {
      color: rgb(var(--v-theme-text-primary))
    }
  }

  :deep(.v-field__input) {
    opacity: 1;
  }

  :deep(.v-field__input::placeholder) {
    color: var(--placeholder-color) !important;
    opacity: 1;
  }

  :deep(.v-field__outline__start),
  :deep(.v-field__outline__end), {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__notch::before) {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__notch::after) {
    border-color: var(--outline-color);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__start) {
    border-left-width: var(--outline-width);
  }

  :deep(.v-field__outline__end) {
    border-right-width: var(--outline-width);
  }

  &.v-input--error :deep(.v-messages) {
    color: rgb(var(--v-theme-text-danger)) !important;
  }
}

:deep(input[type='date']::-webkit-calendar-picker-indicator),
:deep(input[type='time']::-webkit-calendar-picker-indicator) {
  display: none;
}

// Firefox draws a native calendar picker button inside date inputs and
// exposes no pseudo-element to hide it. Clip the input's right edge to mask
// it. Our custom calendar_today icon sits in .v-field__append-inner (a
// separate element) and stays visible.
@supports (-moz-appearance: none) {
  :deep(input[type='date']) {
    clip-path: inset(0 2rem 0 0);
  }
}
</style>

